import { useCallback, useRef, useEffect } from "react"
import type {
	SignalingMessage,
	DataChannelMessage,
	CursorState,
	CursorUserInfo,
	ConnectionState,
} from "./types"
import { resolveApiBaseUrl } from "../../lib/apiClient"

interface UseWebRTCConnectionOptions {
	projectId: string | null
	onCursorUpdate: (cursor: CursorState) => void
	onPeerJoined: (peer: CursorUserInfo) => void
	onPeerLeft: (connectionId: string) => void
	onRoomJoined: (connectionId: string, userInfo: CursorUserInfo) => void
	onConnectionStateChange: (state: ConnectionState) => void
	onLatencyUpdate: (latency: number) => void
}

// Reconnection settings
const MAX_RECONNECT_ATTEMPTS = 5
const BASE_RECONNECT_DELAY = 1000 // 1 second

export function useWebRTCConnection({
	projectId,
	onCursorUpdate,
	onPeerJoined,
	onPeerLeft,
	onRoomJoined,
	onConnectionStateChange,
	onLatencyUpdate,
}: UseWebRTCConnectionOptions) {
	const wsRef = useRef<WebSocket | null>(null)
	const pcRef = useRef<RTCPeerConnection | null>(null)
	const dcRef = useRef<RTCDataChannel | null>(null)
	const fallbackModeRef = useRef(false)
	const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const reconnectAttemptsRef = useRef(0)
	const intentionalDisconnectRef = useRef(false)

	const cleanup = useCallback(() => {
		if (pingIntervalRef.current) {
			clearInterval(pingIntervalRef.current)
			pingIntervalRef.current = null
		}

		if (reconnectTimeoutRef.current) {
			clearTimeout(reconnectTimeoutRef.current)
			reconnectTimeoutRef.current = null
		}

		if (dcRef.current) {
			dcRef.current.close()
			dcRef.current = null
		}

		if (pcRef.current) {
			pcRef.current.close()
			pcRef.current = null
		}

		if (wsRef.current) {
			wsRef.current.close()
			wsRef.current = null
		}

		fallbackModeRef.current = false
	}, [])

	const startPingInterval = useCallback(() => {
		if (pingIntervalRef.current) {
			clearInterval(pingIntervalRef.current)
		}
		pingIntervalRef.current = setInterval(() => {
			if (dcRef.current?.readyState === "open") {
				dcRef.current.send(
					JSON.stringify({
						type: "ping",
						timestamp: Date.now(),
					}),
				)
			}
		}, 5000) // Ping every 5 seconds
	}, [])

	const handleDataChannelMessage = useCallback(
		(data: string) => {
			try {
				const message: DataChannelMessage = JSON.parse(data)

				switch (message.type) {
					case "cursor-update": {
						onCursorUpdate(message.cursor)
						break
					}

					case "cursor-batch": {
						for (const cursor of message.cursors) {
							onCursorUpdate(cursor)
						}
						break
					}

					case "pong": {
						const latency = Date.now() - message.timestamp
						onLatencyUpdate(latency)
						break
					}
				}
			} catch (error) {
				console.error("Failed to parse DataChannel message:", error)
			}
		},
		[onCursorUpdate, onLatencyUpdate],
	)

	const handleOffer = useCallback(
		async (sdp: string) => {
			const pc = new RTCPeerConnection({
				iceServers: [
					{ urls: "stun:stun.l.google.com:19302" },
					{ urls: "stun:stun1.l.google.com:19302" },
				],
			})
			pcRef.current = pc

			// Set up data channel handler
			pc.ondatachannel = (event) => {
				const dc = event.channel
				dcRef.current = dc

				dc.onopen = () => {
					onConnectionStateChange("connected")
					fallbackModeRef.current = false
					reconnectAttemptsRef.current = 0 // Reset on successful connection
					startPingInterval()
				}

				dc.onclose = () => {
					// Fall back to WebSocket
					fallbackModeRef.current = true
					onConnectionStateChange("fallback")
					if (pingIntervalRef.current) {
						clearInterval(pingIntervalRef.current)
						pingIntervalRef.current = null
					}
				}

				dc.onmessage = (event) => {
					handleDataChannelMessage(event.data)
				}
			}

			pc.onicecandidate = (event) => {
				if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
					wsRef.current.send(
						JSON.stringify({
							type: "ice-candidate",
							candidate: event.candidate.toJSON(),
							targetUserId: "server",
						}),
					)
				}
			}

			pc.onconnectionstatechange = () => {
				if (pc.connectionState === "failed") {
					// Fall back to WebSocket
					fallbackModeRef.current = true
					onConnectionStateChange("fallback")
				}
			}

			// Set remote description and create answer
			await pc.setRemoteDescription({ type: "offer", sdp })
			const answer = await pc.createAnswer()
			await pc.setLocalDescription(answer)

			if (wsRef.current?.readyState === WebSocket.OPEN) {
				wsRef.current.send(
					JSON.stringify({
						type: "answer",
						sdp: answer.sdp,
						targetUserId: "server",
					}),
				)
			}
		},
		[onConnectionStateChange, startPingInterval, handleDataChannelMessage],
	)

	const connect = useCallback(async () => {
		if (!projectId) return

		intentionalDisconnectRef.current = false // Reset on connect attempt
		cleanup()
		onConnectionStateChange("connecting")

		// Build WebSocket URL - no token needed, session cookie is sent automatically
		const apiBase = resolveApiBaseUrl()
		const wsUrl =
			apiBase.replace(/^http/, "ws") +
			`/ws/cursor?projectId=${encodeURIComponent(projectId)}`

		const ws = new WebSocket(wsUrl)
		wsRef.current = ws

		ws.onopen = () => {
			onConnectionStateChange("signaling")
		}

		ws.onmessage = async (event) => {
			try {
				const message: SignalingMessage = JSON.parse(event.data)

				switch (message.type) {
					case "room-joined": {
						// Store our server-assigned connectionId
						onRoomJoined(message.connectionId, message.userInfo)
						// Initialize existing peers
						for (const peer of message.peers) {
							onPeerJoined(peer)
						}
						break
					}

					case "peer-joined": {
						onPeerJoined(message.peer)
						break
					}

					case "peer-left": {
						onPeerLeft(message.connectionId)
						break
					}

					case "offer": {
						// Server sent offer - create answer
						await handleOffer(message.sdp)
						break
					}

					case "ice-candidate": {
						if (pcRef.current && message.candidate) {
							try {
								await pcRef.current.addIceCandidate(message.candidate)
							} catch (error) {
								console.error("Failed to add ICE candidate:", error)
							}
						}
						break
					}

					case "fallback-cursor": {
						// Received cursor via WebSocket fallback
						onCursorUpdate(message.cursor)
						break
					}

					case "error": {
						console.error("Cursor error:", message.message)
						if (message.code === "AUTH_FAILED") {
							cleanup()
							onConnectionStateChange("disconnected")
						}
						break
					}
				}
			} catch (error) {
				console.error("Failed to parse WebSocket message:", error)
			}
		}

		ws.onclose = () => {
			onConnectionStateChange("disconnected")
			cleanup()

			// Attempt reconnection with exponential backoff (unless intentionally disconnected)
			if (
				!intentionalDisconnectRef.current &&
				reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS
			) {
				const delay =
					BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current)
				console.log(
					`[Cursor] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/${MAX_RECONNECT_ATTEMPTS})`,
				)
				reconnectTimeoutRef.current = setTimeout(() => {
					reconnectAttemptsRef.current++
					connect()
				}, delay)
			}
		}

		ws.onerror = (error) => {
			console.error("Cursor WebSocket error:", error)
		}
	}, [
		projectId,
		cleanup,
		onConnectionStateChange,
		onPeerJoined,
		onPeerLeft,
		onRoomJoined,
		onCursorUpdate,
		handleOffer,
	])

	const disconnect = useCallback(() => {
		intentionalDisconnectRef.current = true // Prevent auto-reconnect
		reconnectAttemptsRef.current = 0 // Reset attempts

		// Send leave message before closing
		if (wsRef.current?.readyState === WebSocket.OPEN) {
			wsRef.current.send(JSON.stringify({ type: "leave-room" }))
		}
		cleanup()
		onConnectionStateChange("disconnected")
	}, [cleanup, onConnectionStateChange])

	const sendCursor = useCallback((cursor: CursorState) => {
		const message = JSON.stringify({ type: "cursor-update", cursor })

		if (dcRef.current?.readyState === "open") {
			dcRef.current.send(message)
		} else if (
			fallbackModeRef.current &&
			wsRef.current?.readyState === WebSocket.OPEN
		) {
			wsRef.current.send(JSON.stringify({ type: "fallback-cursor", cursor }))
		}
	}, [])

	// Cleanup on unmount
	useEffect(() => {
		return () => cleanup()
	}, [cleanup])

	return {
		connect,
		disconnect,
		sendCursor,
		isConnected: () =>
			dcRef.current?.readyState === "open" || fallbackModeRef.current,
	}
}
